import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';

export const DEFAULT_LABEL = { width: 58, height: 30 };
export const MAX_LABELS = 300;
export const LABEL_DPI = 203;

export function validateLabelSize(size) {
  const width = Number(size.width);
  const height = Number(size.height);
  if (!Number.isFinite(width) || width < 20 || width > 100 ||
      !Number.isFinite(height) || height < 15 || height > 100) {
    throw new Error('Usa un ancho de 20 a 100 mm y un alto de 15 a 100 mm.');
  }
  return { width, height };
}

export function labelQuantity(value) {
  const quantity = Number(value);
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_LABELS) {
    throw new Error(`Indica entre 1 y ${MAX_LABELS} etiquetas por producto.`);
  }
  return quantity;
}

// La etiqueta contiene sólo barras. El SKU exacto se codifica con CODE128;
// el margen blanco y los anchos enteros en puntos evitan recortar o suavizar barras.
// API de datos: https://github.com/lindell/JsBarcode#retrieve-the-barcode-values-so-you-can-render-it-any-way-youd-like
export function buildBarcodeLabel(sku, size = DEFAULT_LABEL) {
  const dimensions = validateLabelSize(size);
  if (typeof sku !== 'string' || !/^[\x20-\x7E]{4,80}$/.test(sku) || sku.trim() !== sku) {
    throw new Error('El producto necesita un SKU de 4 a 80 caracteres, sin acentos ni espacios al inicio o al final. Revísalo en Productos.');
  }
  const encoded = {};
  JsBarcode(encoded, sku, { format: 'CODE128', displayValue: false, margin: 0 });
  const bits = encoded.encodings.map((item) => item.data).join('');
  const widthPx = Math.round(dimensions.width * LABEL_DPI / 25.4);
  const heightPx = Math.round(dimensions.height * LABEL_DPI / 25.4);
  const margin = Math.ceil(2 * LABEL_DPI / 25.4);
  const quietModules = 10;
  const moduleWidth = Math.min(4, Math.floor((widthPx - margin * 2) / (bits.length + quietModules * 2)));
  if (moduleWidth < 2) {
    throw new Error('Este código no cabe con barras legibles. Aumenta el ancho de la etiqueta o asigna un SKU más corto en Productos.');
  }
  const barHeight = Math.min(Math.round(16 * LABEL_DPI / 25.4), heightPx - margin * 2);
  const left = Math.floor((widthPx - bits.length * moduleWidth) / 2);
  const top = Math.floor((heightPx - barHeight) / 2);
  const bars = [];
  for (let i = 0; i < bits.length;) {
    if (bits[i] !== '1') { i += 1; continue; }
    const start = i;
    while (i < bits.length && bits[i] === '1') i += 1;
    bars.push({ x: left + start * moduleWidth, y: top, width: (i - start) * moduleWidth, height: barHeight });
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${dimensions.width}mm" height="${dimensions.height}mm" viewBox="0 0 ${widthPx} ${heightPx}" shape-rendering="crispEdges"><rect width="${widthPx}" height="${heightPx}" fill="white"/><g fill="black">${bars.map((bar) => `<rect x="${bar.x}" y="${bar.y}" width="${bar.width}" height="${bar.height}"/>`).join('')}</g></svg>`;
  return { ...dimensions, widthPx, heightPx, moduleWidth, bars, svg };
}

export function prepareBarcodeBatch(rows, size) {
  validateLabelSize(size);
  if (!rows.length) throw new Error('Agrega al menos un producto.');
  let total = 0;
  const batch = rows.map((row) => {
    const quantity = labelQuantity(row.quantity);
    total += quantity;
    return { ...row, quantity, label: buildBarcodeLabel(row.sku, size) };
  });
  if (total > MAX_LABELS) throw new Error(`Puedes generar hasta ${MAX_LABELS} etiquetas a la vez.`);
  return batch;
}

export function createBarcodePDF(rows, size) {
  const batch = prepareBarcodeBatch(rows, size);
  const { width, height } = validateLabelSize(size);
  const orientation = width >= height ? 'landscape' : 'portrait';
  const doc = new jsPDF({ unit: 'mm', format: [width, height], orientation, compress: true, precision: 5 });
  let page = 0;
  batch.forEach(({ label, quantity }) => {
    for (let i = 0; i < quantity; i += 1) {
      if (page > 0) doc.addPage([width, height], orientation);
      doc.setFillColor(0, 0, 0);
      label.bars.forEach((bar) => {
        doc.rect(bar.x * width / label.widthPx, bar.y * height / label.heightPx,
          bar.width * width / label.widthPx, bar.height * height / label.heightPx, 'F');
      });
      page += 1;
    }
  });
  doc.setProperties({ title: 'Etiquetas de códigos de barras' });
  return doc;
}

export function downloadBarcodePNG(sku, size) {
  const label = buildBarcodeLabel(sku, size);
  const canvas = document.createElement('canvas');
  canvas.width = label.widthPx;
  canvas.height = label.heightPx;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Tu navegador no permite generar la imagen. Prueba descargar el PDF.');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = '#000';
  label.bars.forEach((bar) => context.fillRect(bar.x, bar.y, bar.width, bar.height));
  const link = document.createElement('a');
  link.href = canvas.toDataURL('image/png');
  link.download = `barras_${sku.replace(/[^a-zA-Z0-9_-]/g, '_')}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}
